import classNames from "classnames";
import {
  forwardRef,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from "react";
import { get } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { useOptionalFormContext } from "../hooks/useOptionalFormContext";

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "name"> & {
  className?: string;
  innerClassName?: string;
  name: string;
  label?: string;
  children?: unknown | unknown[];
  isForm?: boolean;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({
  className,
  innerClassName,
  label,
  children,
  name,
  disabled,
  isForm = true,
  ...rest
}: SelectProps, ref) => {
  const {formState} = useOptionalFormContext();
  const [error, setError] = useState<string>();

  useEffect(() => {
    const fieldError = get(formState.errors, name);
    if (fieldError) {
      setError(fieldError.message);
    } else {
      setError(undefined);
    }
  }, [formState, name]);

  const padding = children ? "pl-2 pr-8" : "px-2";

  const style = error
    ? `border-red-500 ${padding}`
    : `border-gray-400 ${padding}`;

  const disabledStyle = disabled
    ? "cursor-not-allowed opacity-75 disabled:text-gray-400 disabled:bg-gray-200 disabled:border-none"
    : "cursor-pointer";

  return (
    <div className={className}>
      {
        !!label &&
        <label className="text-sm" htmlFor={name}>{label}</label>
      }
      <div className="relative w-full select-none">
        <select id={name} name={name} ref={ref} {...rest}  disabled={disabled}
          className={classNames(style, innerClassName,
            "peer appearance-none block w-full border rounded py-0.5",
            disabledStyle
          )}>
          {children}
        </select>
        <div className="pointer-events-none absolute right-0 top-0 py-0.5 px-2">
          <FontAwesomeIcon icon={faChevronDown}/>
        </div>
      </div>
      {
        isForm &&
        <div className={classNames("text-xs text-red-500",
          error ? "" : "hidden"
        )}>
          {error ?? "Valid"}
        </div>
      }
    </div>
  );
});

Select.displayName = "Select";
